import { useState, useEffect } from "react";
import { Search, Filter, Building2, MapPin, Trash2, Download, Plus } from "lucide-react";
import LeadScoreBadge from "../components/leads/LeadScoreBadge";
import AddLeadModal from "../components/leads/AddLeadModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const EXPORT_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/export/leads`;

const STATUS_COLORS = {
  "New":           "bg-blue-100 text-blue-800 border-blue-200",
  "Contacted":     "bg-purple-100 text-purple-800 border-purple-200",
  "Follow-up":     "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Call Booked":   "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Proposal Sent": "bg-orange-100 text-orange-800 border-orange-200",
  "Negotiating":   "bg-amber-100 text-amber-800 border-amber-200",
  "Won":           "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Retainer":      "bg-teal-100 text-teal-800 border-teal-200",
  "Lost":          "bg-gray-100 text-gray-800 border-gray-200",
};

const ICP_COLORS = {
  "ICP-1 International": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "ICP-2 India SMB":     "bg-rose-100 text-rose-800 border-rose-200",
};

export default function Leads() {
  const [leads, setLeads]               = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm]     = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [icpFilter, setIcpFilter]       = useState("all");
  const [showAddLead, setShowAddLead]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  const fetchLeads = async () => {
    try {
      const r = await api.get('/api/leads', { params: { limit: 200 } });
      const data = r.data?.data || r.data;
      setLeads(data);
      setFilteredLeads(data);
    } catch {
      toast.error("Failed to fetch leads");
    }
  };

  useEffect(() => { fetchLeads(); }, []); // eslint-disable-line

  useEffect(() => {
    let f = leads;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      f = f.filter(l =>
        l.company_name?.toLowerCase().includes(q) ||
        l.contact_name?.toLowerCase().includes(q) ||
        l.industry?.toLowerCase().includes(q) ||
        l.country?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") f = f.filter(l => l.status === statusFilter);
    if (icpFilter !== "all")    f = f.filter(l => l.icp_type === icpFilter);
    setFilteredLeads(f);
  }, [searchTerm, statusFilter, icpFilter, leads]);

  const deleteLead = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/leads/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.company_name}`);
      setDeleteTarget(null);
      fetchLeads();
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  return (
    <div className="space-y-4">
      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onAdded={(lead) => { setLeads(l => [lead, ...l]); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900">Leads</h1>
          <p className="text-xs text-zinc-500 mt-0.5">{filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-zinc-300 text-zinc-600"
            onClick={() => window.open(`${EXPORT_URL}?token=${localStorage.getItem('token')}`, '_blank')}>
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setShowAddLead(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input placeholder="Search company, contact, industry, country…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 border-zinc-300" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-44 border-zinc-300">
            <Filter className="w-4 h-4 mr-2 text-zinc-400" /><SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={icpFilter} onValueChange={setIcpFilter}>
          <SelectTrigger className="w-full md:w-48 border-zinc-300">
            <SelectValue placeholder="ICP Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ICP Types</SelectItem>
            <SelectItem value="ICP-1 International">ICP-1 International</SelectItem>
            <SelectItem value="ICP-2 India SMB">ICP-2 India SMB</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-white border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                {['Company', 'Contact', 'ICP / Industry', 'Score', 'Proposal', 'Assigned', 'Status', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredLeads.map(lead => (
                <tr key={lead.id} className="hover:bg-zinc-50 transition-colors">
                  <td className="px-5 py-3">
                    <span className="font-medium text-zinc-900 text-sm">{lead.company_name}</span>
                    {(lead.country || lead.location) && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5">
                        <MapPin className="w-3 h-3" />{lead.country || lead.location}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-sm text-zinc-800">{lead.contact_name}</div>
                    <div className="text-xs text-zinc-400">{lead.designation}</div>
                  </td>
                  <td className="px-5 py-3">
                    {lead.icp_type && (
                      <Badge className={`${ICP_COLORS[lead.icp_type] || 'bg-zinc-100 text-zinc-600 border-zinc-200'} border text-xs`}>
                        {lead.icp_type === 'ICP-1 International' ? 'Intl' : 'India SMB'}
                      </Badge>
                    )}
                    <div className="text-xs text-zinc-500 mt-0.5">{lead.industry}</div>
                  </td>
                  <td className="px-5 py-3"><LeadScoreBadge score={lead.lead_score} /></td>
                  <td className="px-5 py-3">
                    {lead.proposal_value
                      ? <span className="text-sm font-medium text-orange-700">{lead.proposal_value}</span>
                      : <span className="text-sm text-zinc-300">—</span>}
                  </td>
                  <td className="px-5 py-3 text-sm text-zinc-600">{lead.assigned_to || '—'}</td>
                  <td className="px-5 py-3">
                    <Badge className={`${STATUS_COLORS[lead.status] || 'bg-zinc-100 text-zinc-600'} border text-xs`}>
                      {lead.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-zinc-200 h-7 text-xs"
                        onClick={() => navigate(`/leads/${lead.id}`)}>
                        View
                      </Button>
                      <button className="text-zinc-300 hover:text-red-500 transition-colors"
                        onClick={() => setDeleteTarget(lead)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredLeads.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-8 h-8 text-zinc-200 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No leads found.</p>
            </div>
          )}
        </div>
      </Card>

      {/* ── Delete Lead Confirm Sheet ─────────────────────────────────────── */}
      <Sheet open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px]">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={18} /> Delete Lead
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Delete <strong>{deleteTarget?.company_name}</strong>?
            </p>
            <p className="text-sm text-gray-500">This cannot be undone.</p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={deleteLead}>Delete</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
